import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ChatReason } from '@/models/savChat';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-hot-toast';

interface CreateSavChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: ChatReason, reasonDetails: string, initialMessage: string) => Promise<void>;
  orderReference: string;
}

export const CreateSavChatModal: React.FC<CreateSavChatModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  orderReference,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<ChatReason>(ChatReason.OTHER);
  const [reasonDetails, setReasonDetails] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!initialMessage.trim()) {
      toast.error(t('admin.savChat.customer.enterMessageError'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(reason, reasonDetails, initialMessage);
      // Réinitialiser le formulaire
      setReason(ChatReason.OTHER);
      setReasonDetails('');
      setInitialMessage('');
      toast.success(t('admin.savChat.customer.openSuccess'));
    } catch (error) {
      console.error('Erreur lors de la création du chat:', error);
      toast.error(t('admin.savChat.customer.openError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <div>
            <h2 className="text-xl font-bold text-foreground">{t('admin.savChat.openChat')}</h2>
            <p className="text-sm text-foreground-secondary mt-1">
              {t('admin.savChat.admin.info.payment.order')} #{orderReference}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-foreground-secondary" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Motif de contact */}
          <div>
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-foreground mb-2"
            >
              {t('admin.savChat.selectReason')} *
            </label>
            <select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as ChatReason)}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              disabled={isSubmitting}
            >
              {Object.values(ChatReason).map((value) => (
                <option key={value} value={value}>
                  {t(`admin.savChat.reasons.${value}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Détails supplémentaires */}
          <div>
            <label
              htmlFor="reasonDetails"
              className="block text-sm font-medium text-foreground mb-2"
            >
              {t('admin.savChat.reasonDetails')}
            </label>
            <input
              id="reasonDetails"
              type="text"
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              placeholder={t('admin.savChat.reasonDetails')}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-foreground-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              disabled={isSubmitting}
            />
          </div>

          {/* Message initial */}
          <div>
            <label
              htmlFor="initialMessage"
              className="block text-sm font-medium text-foreground mb-2"
            >
              {t('admin.savChat.yourMessage')} *
            </label>
            <textarea
              id="initialMessage"
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder={t('admin.savChat.customer.writeMessagePlaceholder')}
              rows={5}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-foreground-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              disabled={isSubmitting}
              required
            />
            <p className="text-xs text-foreground-secondary mt-1">
              {initialMessage.length} {t('admin.savChat.customer.characters')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-background-secondary text-foreground font-medium rounded-lg hover:bg-background-tertiary transition-colors"
              disabled={isSubmitting}
            >
              {t('admin.savChat.admin.info.refundModal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !initialMessage.trim()}
            >
              {isSubmitting ? t('admin.savChat.customer.opening') : t('admin.savChat.openChat')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

